import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { unwrapPage } from "@/lib/paging";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { FlaskConical, Loader2, Plus, Truck } from "lucide-react";
import { humanizeCode } from "@/lib/utils";
import type { SampleRecord, SampleStatus } from "@/types";
import { SampleStatusBadge } from "@/components/samples/SampleStatusBadge";
import { SampleActions } from "@/components/samples/SampleActions";
import { SampleFormDialog } from "@/components/samples/SampleFormDialog";

const formatDate = (d?: string | null) => (d ? new Date(d.length === 10 ? `${d}T00:00:00` : d).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "");

/** The samples sent to the buyer for this deal, with the next step for each. Approval moves the deal on from Sampling. */
export function DealSamplesCard({ dealId, isClosed, onChanged }: { dealId: string; isClosed: boolean; onChanged: () => void }) {
  const { hasPermission } = useAuth();
  const [samples, setSamples] = useState<SampleRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState<SampleRecord | "new" | null>(null);
  const canRead = hasPermission("sample.read");
  const canCreate = hasPermission("sample.create") && !isClosed;

  const load = useCallback(async () => {
    if (!canRead) return;
    try {
      setSamples(unwrapPage<SampleRecord>(await api.get(`/api/Sample/List?deal_id=${dealId}&page_size=50`)).rows);
    } catch {
      setSamples([]);
    } finally {
      setLoading(false);
    }
  }, [dealId, canRead]);

  useEffect(() => {
    load();
  }, [load]);

  const changed = () => {
    load();
    onChanged();
  };

  if (!canRead) return null;
  // A deal that never had samples stays uncluttered once it is closed
  if (!loading && samples.length === 0 && !canCreate) return null;

  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-semibold flex items-center gap-1.5">
          <FlaskConical className="size-4 text-primary" /> Samples
          {samples.length > 0 && <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{samples.length}</span>}
        </p>
        {canCreate && (
          <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => setDialog("new")}>
            <Plus className="size-3.5" /> Request sample
          </Button>
        )}
      </div>

      {loading ? (
        <Loader2 className="size-4 animate-spin text-muted-foreground" />
      ) : samples.length === 0 ? (
        <p className="text-xs text-muted-foreground">No samples yet. Request one when the buyer wants to see the quality or shade before ordering.</p>
      ) : (
        <ul className="divide-y divide-border">
          {samples.map((s) => (
            <li key={s.id} className="py-2.5 space-y-1.5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium">
                    {s.product_name}
                    {s.variant_name ? <span className="text-muted-foreground font-normal"> · {s.variant_name}</span> : null}
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    <code className="font-mono">{s.sample_number}</code> · {humanizeCode(s.sample_type)}
                    {s.quantity ? ` · ${Number(s.quantity)} ${s.unit || ""}` : ""} · asked {formatDate(s.requested_on)}
                    {s.needed_by ? ` · needed ${formatDate(s.needed_by)}` : ""}
                  </p>
                  {s.dispatched_on && (
                    <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                      <Truck className="size-3" /> {s.courier}
                      {s.awb_number ? ` · AWB ${s.awb_number}` : ""} · {formatDate(s.dispatched_on)}
                    </p>
                  )}
                  {s.buyer_comments && ["REJECTED", "REVISION", "APPROVED"].includes(s.sample_status) && (
                    <p className="text-[11px] text-foreground/80 break-words">Buyer: {s.buyer_comments}</p>
                  )}
                </div>
                <SampleStatusBadge status={s.sample_status as SampleStatus} round={s.round} />
              </div>
              {!isClosed && <SampleActions sample={s} onChanged={changed} onEdit={(x) => setDialog(x)} compact />}
            </li>
          ))}
        </ul>
      )}

      <SampleFormDialog target={dialog} dealId={dealId} onClose={() => setDialog(null)} onSaved={changed} />
    </div>
  );
}
